import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, safeStorage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Mail } from 'lucide-react';

const Login = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });

            // Store session token in localStorage as fallback (cookies may not work in all browsers)
            if (res.data.session_token) {
                safeStorage.setItem('session_token', res.data.session_token);
            }

            toast.success('Connexion réussie !');

            // Redirect based on role
            if (res.data.user.role === 'prestataire') {
                window.location.href = '/prestataire';
            } else {
                window.location.href = '/backoffice';
            }
        } catch (error) {
            toast.error(error.response?.data?.error || error.response?.data?.detail || 'Email ou mot de passe incorrect');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center p-4">
            <Card className="w-full max-w-md shadow-2xl">
                <CardHeader className="text-center space-y-2 pb-8">
                    <div className="mx-auto mb-4">
                        <img
                            src="https://customer-assets.emergentagent.com/job_wedding-erp/artifacts/6y9ywpy7_image.png"
                            alt="Kolnoa"
                            className="h-20 mx-auto"
                        />
                    </div>
                    <CardTitle className="text-3xl font-bold text-stone-800">
                        Connexion Admin Kolnoa ERP
                    </CardTitle>
                    <CardDescription className="text-stone-600">
                        Accédez à votre espace de gestion
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                                <Mail className="h-4 w-4 text-stone-500" />
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="admin@kolnoa.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="h-11"
                                data-testid="input-email"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label htmlFor="password" className="flex items-center gap-2">
                                    <Lock className="h-4 w-4 text-stone-500" />
                                    Mot de passe
                                </Label>
                                <Link to="/forgot-password" className="text-xs text-amber-600 hover:text-amber-700 hover:underline">
                                    Mot de passe oublié ?
                                </Link>
                            </div>
                            <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="h-11"
                                data-testid="input-password"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                            data-testid="btn-login"
                        >
                            {loading ? 'Connexion...' : 'Se connecter'}
                        </Button>
                    </form>

                    {/* Development only - Remove in production */}
                    {process.env.NODE_ENV === 'development' && (
                        <div className="mt-4 pt-4 border-t text-center">
                            <p className="text-xs text-stone-500">
                                💡 Identifiants admin : <code className="bg-stone-100 px-2 py-1 rounded">jeremy.ferrar@gmail.com</code>
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Login;
